import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { UserService, UserType } from './user.service';

export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  children?: MenuItem[];
  permission?: string;
  visible?: boolean;
}

/**
 * Stateful service to manage navigation menu items
 * Menu is determined based on user type and organization type
 */
@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private userService = inject(UserService);

  // BehaviorSubject for observable pattern
  private menuItemsSubject = new BehaviorSubject<MenuItem[]>([]);

  // Observable for components that prefer observables
  public menuItems$ = this.menuItemsSubject.asObservable();

  // Signal for components that prefer signals
  public menuItemsSignal = signal<MenuItem[]>([]);

  // Signal for expanded menu items (by ID)
  public expandedMenuIds = signal<Set<string>>(new Set());

  // Computed signal for visible menu items
  public visibleMenuItems = computed(() =>
    this.menuItemsSignal().filter(item => item.visible !== false)
  );

  constructor() {
    // Update menu when user changes
    effect(() => {
      const user = this.userService.currentUserSignal();
      this.updateMenuForUser(user);
    });
  }

  /**
   * Update menu based on user type and organization type
   */
  private updateMenuForUser(user: any): void {
    if (!user) {
      this.setMenuItems([]);
      return;
    }

    let menu: MenuItem[] = [];

    if (user.userType === UserType.Shopper) {
      menu = this.getMemberMenu();
    } else if (user.userType === UserType.Admin) {
      const orgType = user.organization?.organizationType;

      if (orgType) {
        menu = this.getAdminMenuByOrganizationType(orgType);
      } else {
        menu = this.getDefaultAdminMenu();
      }
    } else {
      // Default menu
      menu = this.getDefaultMenu();
    }

    this.setMenuItems(menu);
  }

  /**
   * Get Member/Shopper menu (from prototype Member tile)
   */
  private getMemberMenu(): MenuItem[] {
    return [
      {
        id: 'shop',
        label: 'Shop',
        icon: 'storefront',
        route: '/shop',
        visible: true,
      },
      {
        id: 'cart',
        label: 'Cart',
        icon: 'shopping_cart',
        route: '/cart',
        visible: true,
      },
      {
        id: 'profile-hub',
        label: 'Profile',
        icon: 'person',
        route: '/profile-hub',
        visible: true,
      },
    ];
  }

  /**
   * Get admin menu based on organization type (dynamic)
   * Organization types come from the database/API, not hardcoded
   */
  private getAdminMenuByOrganizationType(organizationType: string): MenuItem[] {
    // Use organization type to determine menu structure
    // This allows for any organization type from the database
    const orgTypeLower = organizationType.toLowerCase();

    // Map organization type to appropriate menu structure
    // This can be extended to support additional organization types dynamically
    if (orgTypeLower.includes('manufacturer')) {
      return this.getOrganizationAdminMenu('manufacturer');
    } else if (orgTypeLower.includes('distributor')) {
      return this.getOrganizationAdminMenu('distributor');
    } else {
      // Generic admin menu for unknown organization types
      return this.getGenericAdminMenu(organizationType);
    }
  }

  /**
   * Get organization-specific admin menu (generic implementation)
   * Routes are dynamically generated based on organization type prefix
   */
  private getOrganizationAdminMenu(orgTypePrefix: string): MenuItem[] {
    const baseRoute = `/${orgTypePrefix}`;

    // Manufacturer-style menu structure
    if (orgTypePrefix === 'manufacturer') {
      return [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'dashboard',
          route: `${baseRoute}/dashboard`,
          visible: true,
        },
        {
          id: 'users',
          label: 'Users',
          icon: 'groups',
          route: `${baseRoute}/users`,
          visible: true,
        },
        {
          id: 'products',
          label: 'Products',
          icon: 'inventory_2',
          route: `${baseRoute}/products`,
          visible: true,
          children: [
            {
              id: 'products-list',
              label: 'Product List',
              icon: 'list',
              route: `${baseRoute}/products`,
              visible: true,
            },
            {
              id: 'categories',
              label: 'Categories',
              icon: 'category',
              route: `${baseRoute}/products/categories`,
              visible: true,
            },
            {
              id: 'types',
              label: 'Types',
              icon: 'label',
              route: `${baseRoute}/products/types`,
              visible: true,
            },
            {
              id: 'configuration',
              label: 'Configuration',
              icon: 'settings',
              route: `${baseRoute}/products/configuration`,
              visible: true,
            },
          ],
        },
        {
          id: 'embroidery',
          label: 'Embroidery',
          icon: 'auto_awesome',
          route: `${baseRoute}/embroidery`,
          visible: true,
          children: [
            {
              id: 'embroidery-dashboard',
              label: 'Dashboard',
              icon: 'dashboard',
              route: `${baseRoute}/embroidery`,
              visible: true,
            },
            {
              id: 'locations',
              label: 'Locations',
              icon: 'location_on',
              route: `${baseRoute}/embroidery/locations`,
              visible: true,
            },
            {
              id: 'pricing',
              label: 'Pricing',
              icon: 'attach_money',
              route: `${baseRoute}/embroidery/pricing`,
              visible: true,
            },
            {
              id: 'threads',
              label: 'Thread Colors',
              icon: 'palette',
              route: `${baseRoute}/embroidery/threads`,
              visible: true,
            },
            {
              id: 'fonts',
              label: 'Fonts',
              icon: 'text_fields',
              route: `${baseRoute}/embroidery/fonts`,
              visible: true,
            },
            {
              id: 'settings',
              label: 'Settings',
              icon: 'settings',
              route: `${baseRoute}/embroidery/settings`,
              visible: true,
            },
          ],
        },
        {
          id: 'inventory',
          label: 'Inventory',
          icon: 'warehouse',
          route: `${baseRoute}/inventory`,
          visible: true,
        },
        {
          id: 'orders',
          label: 'Orders',
          icon: 'shopping_cart',
          route: `${baseRoute}/orders`,
          visible: true,
        },
        {
          id: 'branding',
          label: 'Branding',
          icon: 'palette',
          route: `${baseRoute}/branding`,
          visible: true,
        },
      ];
    }

    // Distributor-style menu structure
    if (orgTypePrefix === 'distributor') {
      return [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'dashboard',
          route: `${baseRoute}/dashboard`,
          visible: true,
        },
        {
          id: 'users',
          label: 'Users',
          icon: 'groups',
          route: `${baseRoute}/users`,
          visible: true,
        },
        {
          id: 'members',
          label: 'Members',
          icon: 'people',
          route: `${baseRoute}/members`,
          visible: true,
        },
        {
          id: 'manufacturers',
          label: 'Manufacturers',
          icon: 'factory',
          route: `${baseRoute}/manufacturers`,
          visible: true,
        },
        {
          id: 'products',
          label: 'Products',
          icon: 'inventory_2',
          route: `${baseRoute}/products`,
          visible: true,
        },
        {
          id: 'orders',
          label: 'Orders',
          icon: 'shopping_cart',
          route: `${baseRoute}/orders`,
          visible: true,
        },
        {
          id: 'settings',
          label: 'Settings',
          icon: 'settings',
          route: `${baseRoute}/settings`,
          visible: true,
        },
        {
          id: 'branding',
          label: 'Branding',
          icon: 'palette',
          route: `${baseRoute}/branding`,
          visible: true,
        },
      ];
    }

    // Fallback for unknown organization types
    return this.getGenericAdminMenu(orgTypePrefix);
  }

  /**
   * Get generic admin menu for unknown organization types
   */
  private getGenericAdminMenu(organizationType: string): MenuItem[] {
    const baseRoute = `/${organizationType.toLowerCase()}`;
    return [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'dashboard',
        route: `${baseRoute}/dashboard`,
        visible: true,
      },
      {
        id: 'users',
        label: 'Users',
        icon: 'groups',
        route: `${baseRoute}/users`,
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: 'settings',
        route: `${baseRoute}/settings`,
        visible: true,
      },
    ];
  }

  /**
   * Get default admin menu (fallback)
   */
  private getDefaultAdminMenu(): MenuItem[] {
    return [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'dashboard',
        route: '/dashboard',
        visible: true,
      },
    ];
  }

  /**
   * Get default menu (fallback)
   */
  private getDefaultMenu(): MenuItem[] {
    return [
      {
        id: 'home',
        label: 'Home',
        icon: 'home',
        route: '/home',
        visible: true,
      },
    ];
  }

  /**
   * Set menu items
   */
  setMenuItems(items: MenuItem[]): void {
    this.menuItemsSubject.next(items);
    this.menuItemsSignal.set(items);
  }

  /**
   * Get all menu items
   */
  getMenuItems(): MenuItem[] {
    return this.menuItemsSubject.value;
  }

  /**
   * Toggle menu item expansion
   */
  toggleExpanded(menuId: string): void {
    const expanded = new Set(this.expandedMenuIds());
    if (expanded.has(menuId)) {
      expanded.delete(menuId);
    } else {
      expanded.add(menuId);
    }
    this.expandedMenuIds.set(expanded);
  }

  /**
   * Check if menu item is expanded
   */
  isExpanded(menuId: string): boolean {
    return this.expandedMenuIds().has(menuId);
  }

  /**
   * Expand a menu item
   */
  expand(menuId: string): void {
    const expanded = new Set(this.expandedMenuIds());
    expanded.add(menuId);
    this.expandedMenuIds.set(expanded);
  }

  /**
   * Collapse a menu item
   */
  collapse(menuId: string): void {
    const expanded = new Set(this.expandedMenuIds());
    expanded.delete(menuId);
    this.expandedMenuIds.set(expanded);
  }

  /**
   * Collapse all menu items
   */
  collapseAll(): void {
    this.expandedMenuIds.set(new Set());
  }


  /**
   * Filter menu items by permissions
   */
  private filterMenuByPermissions(items: MenuItem[], permissions: string[]): MenuItem[] {
    return items
      .filter(item => !item.permission || permissions.includes(item.permission))
      .map(item => ({
        ...item,
        children: item.children
          ? this.filterMenuByPermissions(item.children, permissions)
          : undefined,
      }));
  }
}
